import { guid } from 'src/app/common/utils/guid';
import { VisitorService } from './../../services/visitor.service';
import { SocialLoginComponent } from './../social.login/social.login.component';
import { isEmptyArray, upperFirstLetter } from 'src/app/common/utils/object.extensions';
import { BaseComponent } from 'src/app/common/components/base.component';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { VisitorEvent } from '../../models/visitor.event';
import { ActivatedRoute } from '@angular/router';
import {
  UserTrip,
  TripStorageCommand,
  UserTripModel,
  CreateTripCommand,
} from '../../models/vititor.trip.storage.command';
import { ConfigService } from 'src/app/common/services/config/config.service';
import { switchMap, timer, takeUntil, share, tap } from 'rxjs';
import { VisitorPlace } from '../../models/visitor.place';
import { EventTimeService } from '../../../event-calendar/services/event.time.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'ig-add-to-trip',
  templateUrl: './add.to.trip.component.html',
  styleUrls: ['./add.to.trip.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AddToTripComponent extends BaseComponent implements AfterViewInit, OnChanges {
  public isInAnyTrip = false;
  public isAddingNewTrip = false;
  public isLoadingTrips = false;
  public trips: UserTripModel[] = [];
  public newTripTitle = '';
  public tripFormGroup: FormGroup;
  public isTripDetailsMode = false;
  public isMenuOpened = false;

  @Input() public item: VisitorEvent | VisitorPlace;
  @Input() public showItemType = true;
  @Input() public isNeedLogin = true;
  @Input() public forTripPlannerV3 = false;
  @Input() public highlightBorderTop = false;
  @Input() public mode: 'normal' | 'half' = 'normal';

  @Input() set userTrips(trips: UserTrip[] | UserTripModel[] | null) {
    if (!trips) {
      this.trips = null;
      return;
    }
    // Convert UserTrip[] to UserTripModel[] by adding required properties if they don't exist
    this._trips = trips.map((trip) => {
      if ('isShowingDetails' in trip && 'isSuccess' in trip) {
        return trip as UserTripModel;
      }
      return {
        ...trip,
        isShowingDetails: false,
        isSuccess: false,
      } as UserTripModel;
    });
    this.isLoadingTrips = false;
  }
  get userTrips(): UserTripModel[] | null {
    return this._trips;
  }
  private _trips: UserTripModel[] | null = null;

  @Output() loginChanged = new EventEmitter<boolean>();
  @Output() onAddTrip = new EventEmitter<any>();

  @ViewChild('menuTrigger', { static: true })
  public menuTrigger: MatMenuTrigger;
  @ViewChild('logindiv', { static: false })
  public logindiv: ElementRef;
  @ViewChild('deleteMenuTrigger', { static: false })
  public deleteMenuTrigger: MatMenuTrigger;
  @ViewChildren('buttons')
  public buttons: QueryList<ElementRef>;
  @ViewChild('socialLogin', { static: false })
  socialLogin: SocialLoginComponent;

  constructor(
    public activatedRoute: ActivatedRoute,
    public visitorService: VisitorService,
    public timeService: EventTimeService,
    private formBuilder: FormBuilder,
    private ref: ChangeDetectorRef
  ) {
    super({ activatedRoute });

    this.tripFormGroup = this.formBuilder.group({
      tripTitle: [null, [Validators.required]],
    });
  }

  async ngAfterViewInit() {
    this.trips = this.userTrips || (await this.loadTrips());
    this.checkIsInAnyTrip();
  }

  public get isUserLoggedIn() {
    return SocialLoginComponent.isUserLoggedIn;
  }

  public async loginStatusChanged(status: boolean) {
    // for v3, the user trips are loaded in trip.planner.component, so here is to notify it to reload user trips and assign values to this.trips in ngOnChanges
    if (this.forTripPlannerV3) {
      this.loginChanged.emit(status);
    }
    console.log('loginStatusChanged in add to trip', this.userTrips);
    this.trips = this.userTrips || (await this.loadUserTripsFromDB());
    this.checkIsInAnyTrip();
    this.ref.detectChanges();
  }

  public get inTripText() {
    if (this.mode === 'half') {
      return '- MY TRIP';
    }
    return this.showItemType
      ? `This ${upperFirstLetter(this.itemType())} is in My Trip`
      : 'In my trip';
  }

  public get addToTripText() {
    if (this.mode === 'half') {
      return '+ MY TRIP';
    }
    return this.showItemType
      ? `Add This ${upperFirstLetter(this.itemType())} to My Trip`
      : 'Add to my trip';
  }

  public get isOverLimit() {
    return this.trips?.length >= ConfigService.config.tripPlanner.maxTripCount;
  }

  public getUserInitials(): string {
    if (!SocialLoginComponent.isUserLoggedIn) return 'U';
    const name = SocialLoginComponent.userName || 'User';
    return name
      .split(' ')
      .map((n) => n.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  public getUserDisplayName(): string {
    if (!SocialLoginComponent.isUserLoggedIn) return 'Guest User';
    return SocialLoginComponent.userName || 'User';
  }

  public getUserEmail(): string {
    if (!SocialLoginComponent.isUserLoggedIn) return '';
    return SocialLoginComponent.email || '';
  }

  public async openTripMenu() {
    this.trips = this.userTrips || (await this.loadUserTripsFromDB());
    this.checkIsInAnyTrip();
    this.menuTrigger.openMenu();
    this.isMenuOpened = true;
    this.tripFormGroup.get('tripTitle')?.markAsUntouched();

    // initialize google login again, otherwise only the first google login button can be shown on the page
    setTimeout(() => {
      this.socialLogin?.initGoogleLogin();
    });
  }

  public onClose() {
    this.trips.forEach((x) => {
      x.isSuccess = false;
    });
  }

  openLoginMenu($event) {
    // initialize google login again, otherwise only the first google login button can be shown on the page
    setTimeout(() => {
      this.socialLogin?.initGoogleLogin();
    });
  }

  public tripTitleWithStops(trip: UserTrip) {
    if (this.forTripPlannerV3) {
      let total = 0;
      for (const day of trip.days) {
        total += day.attractions?.length || 0;
      }
      return `${trip.title} (${trip.days.length} day${
        trip.days.length > 1 ? 's' : ''
      }, ${total} stop${total > 1 ? 's' : ''})`;
    }

    return `${trip.title} (${trip.attractions?.length || 0} stop${
      (trip.attractions?.length || 0) > 1 ? 's' : ''
    })`;
  }

  public tripTitleInputFocus($event) {
    $event.stopPropagation();
  }

  public addNewTrip() {
    this.isAddingNewTrip = true;
  }

  public saveNewTrip() {
    if (this.tripFormGroup.invalid) return;

    this.isAddingNewTrip = false;
    this.createNewTrip(this.tripFormGroup.get('tripTitle').value);
    this.tripFormGroup.get('tripTitle').setValue('');
    // this.scrollToTrip(this.trips.length - 1);

    // this.onAddTrip.emit();
  }

  public createNewTrip(title: string) {
    // if (this.forTripPlannerV3) {
    //   this.onAddTrip.emit();
    //   return;
    // }

    if (!SocialLoginComponent.isUserLoggedIn) {
      this.createTripLocal(title);
      this.tripFormGroup.get('tripTitle')?.markAsUntouched();
      return;
    }

    const newDate = new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000);

    const item = {
      title,
      userFacebookId: SocialLoginComponent.userId,
      hubName: this.hubName,
      attractions: [],
      days: [
        {
          date: newDate,
          dateText: this.timeService.getOnlyDisplayDate(newDate),
          note: '',
          attractions: [],
        },
      ],
    } as UserTripModel;

    if (!this.trips) {
      this.trips = [item];
    } else {
      this.trips = [...this.trips, item];
    }

    // don't know why change the trips not trigger the view change (for a new Google user, create the first trip), need to force it
    this.logindiv?.nativeElement?.click();

    if (SocialLoginComponent.isUserLoggedIn) {
      const command = {
        id: guid(),
        userFacebookId: SocialLoginComponent.userId,
        newTrip: {
          title,
          userFacebookId: SocialLoginComponent.userId,
          hubName: this.hubName,
        } as UserTrip,
      } as CreateTripCommand;

      this.isLoadingTrips = true;
      this.visitorService
        .createTrip(this.hubName, command)
        .pipe(
          takeUntil(this.isDestroyed),
          tap(async () => {
            this.tripFormGroup.get('tripTitle')?.markAsUntouched();
            // this.trips = await this.loadUserTripsFromDB();
            this.onAddTrip.emit();
            this.isLoadingTrips = false;
          })
        )
        .subscribe();
    }
  }

  createTripLocal(title: string) {
    const newTrip = {
      id: guid(),
      title: title,
      hubName: this.hubName,
      days: [],
      attractions: [], // Initialize attractions array to prevent undefined errors
      createdAt: new Date().toISOString(),
      userFacebookId: null,
      travelMode: null,
      status: 'local',
    } as UserTrip;

    let trips = this.loadLocalTrips();
    trips.push(newTrip);
    this.trips = trips;
    localStorage.setItem('localTrips', JSON.stringify(this.trips));

    this.onAddTrip.emit();
  }

  saveTripToDB(trip: UserTrip) {
    if (!SocialLoginComponent.isUserLoggedIn || trip.status === 'syncing') {
      return;
    }

    trip.status = 'syncing';

    this.visitorService.saveTrip(this.hubName, trip, SocialLoginComponent.userId).subscribe({
      next: () => {
        trip.status = 'synced';

        setTimeout(() => {
          trip.status = 'cloud';

          this.deleteLocalTrip(trip);
          // this.loadTrips();
          this.onAddTrip.emit();
        }, 3000);
      },
      error: () => {
        trip.status = 'local';
      },
    });
  }

  public isInTrip(tripIndex: number) {
    return this.isInTripV3(tripIndex);

    // if (this.forTripPlannerV3) {
    //   return this.isInTripV3(tripIndex);
    // }

    // return this.trips[tripIndex].attractions.some(
    //   (x) => x.feedId === this.item._id
    // );
  }

  public isInTripV3(tripIndex: number) {
    let isIn = false;
    for (const day of this.trips[tripIndex].days) {
      if (day.attractions && day.attractions.some((x) => x.feedId === this.item._id)) {
        isIn = true;
      }
    }

    return isIn;
  }

  public checkIsInAnyTrip() {
    if (this.forTripPlannerV3) {
      this.checkIsInAnyTripV3();
      return;
    }

    if (!this.trips || isEmptyArray(this.trips)) {
      this.isInAnyTrip = false;
      return;
    }
    this.isInAnyTrip = false;
    this.trips.forEach((trip) => {
      if (trip.attractions && trip.attractions.any((x) => x.feedId === this.item._id)) {
        this.isInAnyTrip = true;
      }
    });

    // Force change detection to ensure UI updates
    this.ref.detectChanges();
  }

  public checkIsInAnyTripV3() {
    if (!this.trips || isEmptyArray(this.trips)) {
      this.isInAnyTrip = false;
      return;
    }
    let isInAnyTrip = false;
    this.trips.forEach((trip) => {
      trip.days.forEach((day) => {
        if (day.attractions && day.attractions.any((x) => x.feedId === this.item._id)) {
          isInAnyTrip = true;
        }
      });
    });
    this.isInAnyTrip = isInAnyTrip;

    // Force change detection to ensure UI updates
    this.ref.detectChanges();
  }

  public addOrRemoveFromTrip($event, tripIndex) {
    $event.stopPropagation && $event.stopPropagation();

    let notReachLimit = true;
    if (this.isInTrip(tripIndex)) {
      this.removeFromTrip(tripIndex);
    } else {
      notReachLimit = this.addItemToTrip(tripIndex);
    }

    if (notReachLimit) {
      // Add a small delay to ensure trip state is fully updated before checking
      setTimeout(() => {
        this.checkIsInAnyTrip();
      }, 100);
      this.showSuccessMsg(tripIndex);
      this.scrollToTrip(tripIndex);
    }
  }

  public addItemToTrip(tripIndex: number): boolean {
    if (this.forTripPlannerV3) {
      if (!this.trips[tripIndex].days || !this.trips[tripIndex].days.length) {
        const newDate = new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000);
        this.trips[tripIndex].days = [
          {
            date: newDate,
            dateText: this.timeService.getOnlyDisplayDate(newDate),
            note: '',
            attractions: [],
          },
        ];
      }

      if (
        this.trips[tripIndex].days[0].attractions &&
        this.trips[tripIndex].days[0].attractions.length >= 20
      ) {
        this.snackBar.open(
          'You have reached the maximum number of attractions for that day!',
          'Close',
          {
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: 'ig-error-snackbar',
          }
        );
        return false;
      }

      if (this.trips[tripIndex].status === 'local') {
        let trips = [];
        const localTrips = localStorage.getItem('localTrips');
        if (localTrips) {
          trips = JSON.parse(localTrips);
        }

        const trip = trips.find((x) => x.id === this.trips[tripIndex].id);
        if (trip) {
          if (!trip.days.length) {
            const newDate = new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000);
            trip.days = [
              {
                date: newDate,
                dateText: this.timeService.getOnlyDisplayDate(newDate),
                note: '',
                attractions: [],
              },
            ];
          }
          if (trip.days[0].attractions && !trip.days[0].attractions.includes(this.item._id)) {
            trip.days[0].attractions.push({
              feedType: this.itemType(),
              feedId: this.item._id,
              order: this.trips[tripIndex].days[0].attractions?.length || 0,
              item: this.item,
            });

            // Also update the current trip in this.trips to ensure UI reflects the change
            if (!this.trips[tripIndex].days[0].attractions) {
              this.trips[tripIndex].days[0].attractions = [];
            }
            this.trips[tripIndex].days[0].attractions.push({
              feedType: this.itemType(),
              feedId: this.item._id,
              order: this.trips[tripIndex].days[0].attractions.length,
              item: this.item,
            });

            // Force change detection to ensure UI updates
            this.ref.detectChanges();
          }
          localStorage.setItem('localTrips', JSON.stringify(trips));

          const tripDB = this.trips.filter((x) => x.status !== 'local');
          this.trips = [...tripDB, ...trips];
          this.userTrips = this.trips;

          // Force change detection to ensure UI updates
          this.ref.detectChanges();
        }
        return true;
      }

      if (this.trips[tripIndex].days[0].attractions) {
        this.trips[tripIndex].days[0].attractions.push({
          feedType: this.itemType(),
          feedId: this.item._id,
          order: this.trips[tripIndex].days[0].attractions?.length || 0,
          item: this.item,
        });
      }

      if (SocialLoginComponent.isUserLoggedIn) {
        this.visitorService
          .addAttractionToTrip(this.hubName, {
            userFacebookId: SocialLoginComponent.userId,
            tripId: this.trips[tripIndex].id,
            attraction: {
              feedType: this.itemType(),
              feedId: this.item._id,
              order: this.trips[tripIndex].days[0].attractions?.length || 0,
            },
          })
          .subscribe(() => {
            this.userTrips = this.trips;

            // Force change detection to ensure UI updates
            this.ref.detectChanges();
          });
      }

      // Force change detection to ensure UI updates
      this.ref.detectChanges();

      return true;
    }

    // Legacy trip handling - ensure attractions array exists
    if (!this.trips[tripIndex].attractions) {
      this.trips[tripIndex].attractions = [];
    }

    this.trips[tripIndex].attractions.push({
      feedType: this.itemType(),
      feedId: this.item._id,
      order: this.trips[tripIndex].attractions.length,
      item: this.item,
    });

    if (SocialLoginComponent.isUserLoggedIn) {
      this.visitorService
        .addAttractionToTrip(this.hubName, {
          userFacebookId: SocialLoginComponent.userId,
          tripId: this.trips[tripIndex].id,
          attraction: {
            feedType: this.itemType(),
            feedId: this.item._id,
            order: this.trips[tripIndex].attractions.length,
          },
        })
        .subscribe(() => {
          // Force change detection to ensure UI updates
          this.ref.detectChanges();
        });
    }

    // Force change detection to ensure UI updates
    this.ref.detectChanges();
    // this.addToLocalStorage(item, tripIndex);

    return true;
  }

  public removeFromTrip(tripIndex: number) {
    if (this.forTripPlannerV3) {
      const trip = this.trips[tripIndex];
      if (trip.status === 'local') {
        let trips = [];
        const localTrips = localStorage.getItem('localTrips');
        if (localTrips) {
          trips = JSON.parse(localTrips);
        }

        const tripLocal = trips.find((x) => x.id === trip.id);
        if (tripLocal) {
          for (const day of tripLocal.days) {
            if (day.attractions) {
              const index = day.attractions.getElementIndex((x) => x.feedId === this.item._id);
              if (index >= 0) {
                day.attractions.splice(index, 1);
              }
            }
          }
          localStorage.setItem('localTrips', JSON.stringify(trips));
        }
        const tripDB = this.trips.filter((x) => x.status !== 'local');
        this.trips = [...tripDB, ...trips];
        this.userTrips = this.trips;

        // Also update the current trip in this.trips to ensure UI reflects the change
        for (const day of this.trips[tripIndex].days) {
          if (day.attractions) {
            const index = day.attractions.getElementIndex((x) => x.feedId === this.item._id);
            if (index >= 0) {
              day.attractions.splice(index, 1);
            }
          }
        }

        // Force change detection to ensure UI updates
        this.ref.detectChanges();
        // this.onAddTrip.emit();
        return;
      }

      for (const day of this.trips[tripIndex].days) {
        if (day.attractions) {
          const index = day.attractions.getElementIndex((x) => x.feedId === this.item._id);
          if (index >= 0) {
            day.attractions.splice(index, 1);
          }
        }
      }
      if (SocialLoginComponent.isUserLoggedIn) {
        this.removeTripItemFromDB(tripIndex);
      }

      // Force change detection to ensure UI updates
      this.ref.detectChanges();
      return;
    }

    if (this.trips[tripIndex] && this.trips[tripIndex].attractions.length) {
      let index = this.trips[tripIndex].attractions.getElementIndex(
        (x) => x.feedId === this.item._id
      );
      this.trips[tripIndex].attractions.splice(index, 1);
    }
    if (SocialLoginComponent.isUserLoggedIn) {
      this.removeTripItemFromDB(tripIndex);
    }

    // Force change detection to ensure UI updates
    this.ref.detectChanges();
    // this.removeFromLocalStorage(tripIndex);
  }

  async deleteLocalTrip(trip: UserTrip) {
    try {
      let trips = this.loadLocalTrips();
      const index = trips.findIndex((t) => t.id === trip.id);
      if (index >= 0) {
        trips.splice(index, 1);
      }
      localStorage.setItem('localTrips', JSON.stringify(trips));
      this.trips = await this.loadTrips();
    } catch (e) {
      console.error('Error deleting trip from localStorage', e);
    }
  }

  public async deleteTrip(trip: UserTripModel) {
    if (trip.status === 'local') {
      await this.deleteLocalTrip(trip);
      this.onAddTrip.emit();
      return;
    }

    if (SocialLoginComponent.userId) {
      this.isLoadingTrips = true;
      this.visitorService
        .deleteTrip(this.hubName, SocialLoginComponent.userId, trip.id)
        .subscribe((data) => {
          // this.trips = data.trips;
          this.deleteMenuTrigger?.closeMenu();
          this.tripFormGroup.get('tripTitle')?.markAsUntouched();

          this.isLoadingTrips = false;
          this.onAddTrip.emit();
        });
    }

    const index = this.trips.findIndex((t) => t.id === trip.id);
    this.trips.splice(index, 1);
    // this.updateTripInLS();
    this.checkIsInAnyTrip();
    // // if all trips are deleted, back to the create trip form
    this.tripFormGroup.get('tripTitle')?.markAsUntouched();
  }

  private removeTripItemFromDB(tripIndex) {
    if (this.forTripPlannerV3) {
      this.visitorService
        .removeAttractionFromTripV3(this.hubName, {
          feedId: this.item._id,
          userFacebookId: SocialLoginComponent.userId,
          tripIndex,
        })
        .subscribe(() => {
          // this.onAddTrip.emit();
          this.userTrips = this.trips;

          // Force change detection to ensure UI updates
          this.ref.detectChanges();
        });
      return;
    }

    this.visitorService
      .removeAttractionFromTrip(this.hubName, {
        feedId: this.item._id,
        userFacebookId: SocialLoginComponent.userId,
        tripIndex,
      })
      .subscribe(() => {
        // Force change detection to ensure UI updates
        this.ref.detectChanges();
      });
  }

  private async loadTrips(): Promise<UserTripModel[]> {
    const tripsInDB = await this.loadUserTripsFromDB();
    const tripsInLocal = this.loadLocalTrips();
    return [...tripsInDB, ...tripsInLocal];
  }

  loadLocalTrips() {
    let trips = [];
    const localTrips = localStorage.getItem('localTrips');
    if (localTrips) {
      trips = JSON.parse(localTrips);
    }
    return trips;
  }

  private async loadUserTripsFromDB(): Promise<UserTripModel[]> {
    // let key = this.getLSKeyForTrip();
    // let trips = JSON.parse(localStorage.getItem(key)) as TripStorageCommand;
    // if (!trips || isEmptyArray(trips.trips)) {
    //   trips = new TripStorageCommand();
    // }
    // return trips.trips;

    if (!SocialLoginComponent.userId) return [];

    this.isLoadingTrips = true;
    let trips: TripStorageCommand;
    try {
      // Try v3 first
      trips = await this.visitorService.getUserTripsV3(this.hubName, SocialLoginComponent.userId);
      // If v3 returns no trips or malformed, throw to fallback
      if (!trips || !Array.isArray(trips.trips)) throw new Error('Invalid v3 response');
      // Mark component to operate in v3 mode from now on
      this.forTripPlannerV3 = true;
    } catch (e) {
      // Fallback to legacy
      trips = await this.visitorService.getUserTrips(this.hubName, SocialLoginComponent.userId);
      this.forTripPlannerV3 = false;
    }
    console.log('trips', trips);

    // const key = this.getLSKeyForTrip();
    // localStorage.setItem(key, JSON.stringify(trips));

    this.isLoadingTrips = false;
    return trips ? trips.trips || [] : [];
  }

  private async loadUserTripsFromLocalStorage() {
    const key = this.getLSKeyForTrip();
    let trip = JSON.parse(localStorage.getItem(key)) as TripStorageCommand;
    if (!trip || isEmptyArray(trip.trips)) {
      trip = new TripStorageCommand();
    }
    return trip;
  }

  // public removeFromLocalStorage(id: string, tripIndex?: number) {
  //   let key = this.getLSKeyForTrip();
  //   // let keyWithUserID = LocalStorageService.getLocalStorageKeyForVisitorTrip(this.hubName, SocialLoginPopupComponent.userId);
  //   let trip = JSON.parse(localStorage.getItem(key)) as TripStorageCommand;

  //   if (!trip || !trip.trips || trip.trips.length <= tripIndex) {
  //     return;
  //   }

  //   if (trip.trips[tripIndex] && trip.trips[tripIndex].attractions.length) {
  //     let index = trip.trips[tripIndex].attractions.getElementIndex(
  //       (x) => x.feedId === id
  //     );
  //     trip.trips[tripIndex].attractions.splice(index, 1);
  //   }
  //   trip.trips[tripIndex].attractions =
  //     trip.trips[tripIndex].attractions.removeDuplicate();
  //   trip.timestamp = new Date();
  //   localStorage.setItem(key, JSON.stringify(trip));
  //   // localStorage.setItem(keyWithUserID, JSON.stringify(trip));

  //   this.trips = trip.trips;
  // }

  // private async addNewTripToLocalStorage(
  //   title: string,
  //   item?: VisitorEvent,
  //   type?: string
  // ): Promise<TripStorageCommand> {
  //   const key = this.getLSKeyForTrip();
  //   let userTrip = JSON.parse(localStorage.getItem(key)) as TripStorageCommand;
  //   if (!userTrip || isEmptyArray(userTrip.trips)) {
  //     userTrip = new TripStorageCommand();
  //   } else {
  //     if (
  //       userTrip.trips.length >= ConfigService.config.tripPlanner.maxTripCount
  //     ) {
  //       return userTrip;
  //     }
  //   }

  //   const newTrip = this.createTrip(title, item, type);
  //   userTrip.trips.push(newTrip);

  //   localStorage.setItem(key, JSON.stringify(userTrip));
  //   // let tripsWithUserId = clone(userTrip);
  //   // tripsWithUserId.userFacebookId = SocialLoginPopupComponent.userId || null;
  //   // localStorage.setItem(keyWithUserID, JSON.stringify(tripsWithUserId));

  //   return userTrip;
  // }

  // private updateTripInLS() {
  //   let key = this.getLSKeyForTrip();
  //   let trip = JSON.parse(localStorage.getItem(key)) as TripStorageCommand;
  //   // let keyWithUserID = LocalStorageService.getLocalStorageKeyForVisitorTrip(this.hubName, SocialLoginPopupComponent.userId);
  //   trip.trips = this.trips;
  //   localStorage.setItem(key, JSON.stringify(trip));
  //   // localStorage.setItem(keyWithUserID, JSON.stringify(trip));
  // }

  // private createTrip(
  //   title: string,
  //   item?: VisitorEvent,
  //   type?: string
  // ): UserTripModel {
  //   let newTrip = new UserTripModel();
  //   newTrip.attractions = [];
  //   if (item) {
  //     newTrip.attractions.push({
  //       feedType: type,
  //       feedId: item._id,
  //       order: 1,
  //     });
  //   }
  //   newTrip.title = title;
  //   newTrip.timestamp = new Date();
  //   newTrip.hubName = this.hubName;
  //   return newTrip;
  // }

  // private addToLocalStorage(item: VisitorEvent, tripIndex?: number) {
  //   let key = this.getLSKeyForTrip();
  //   // let keyWithUserID = LocalStorageService.getLocalStorageKeyForVisitorTrip(this.hubName, SocialLoginPopupComponent.userId);
  //   let trip = JSON.parse(localStorage.getItem(key)) as TripStorageCommand;
  //   if (!trip || !trip.trips) {
  //     return;
  //   }

  //   if (tripIndex >= trip.trips.length) {
  //     return;
  //   }

  //   // trip.trips[tripIndex].attractions = trip.trips[tripIndex].attractions.removeDuplicate();

  //   trip.trips[tripIndex].attractions.push({
  //     feedType: this.itemType(),
  //     feedId: item._id,
  //     order: trip.trips[tripIndex].attractions.length,
  //   });
  //   trip.trips[tripIndex].attractions =
  //     trip.trips[tripIndex].attractions.removeDuplicate();
  //   trip.trips[tripIndex].timestamp = new Date();
  //   trip.trips[tripIndex].hubName = this.hubName;
  //   localStorage.setItem(key, JSON.stringify(trip));
  //   // trip.userFacebookId = SocialLoginPopupComponent.userId || null;
  //   // localStorage.setItem(keyWithUserID, JSON.stringify(trip));
  // }

  private showSuccessMsg(tripIndex: number) {
    this.trips[tripIndex].isShowingDetails = false;
    this.trips[tripIndex].isSuccess = true;
    setTimeout(() => {
      this.trips[tripIndex].isSuccess = false;
    }, 4000);
  }

  private scrollToTrip(tripIndex: number) {
    if (Array.from(this.buttons)[tripIndex]) {
      setTimeout(() => {
        this.scrollIntoView(Array.from(this.buttons)[tripIndex]['_elementRef']);
      }, 100);
    }
  }

  private scrollIntoView(element: ElementRef) {
    element.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  private getLSKeyForTrip(): string {
    return SocialLoginComponent.isUserLoggedIn
      ? 'visitorTrip_' + this.hubName + '_' + SocialLoginComponent.userId + '_v2'
      : `visitorTrip_${this.hubName}_v3`;
  }

  private itemType(): 'event' | 'place' {
    return this.item.startTime ? 'event' : 'place';
  }

  async ngOnChanges(changes: SimpleChanges) {
    console.log('ngOnChanges');
    this.trips = this.userTrips || (await this.loadUserTripsFromDB());
    this.checkIsInAnyTrip();
  }
}
